import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaClock, 
  FaFacebook, 
  FaInstagram, 
  FaLinkedin, 
  FaWhatsapp,
  FaCalendarAlt
} from 'react-icons/fa';

const Contact = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const elements = sectionRef.current.querySelectorAll('.fade-in, .scroll-fade-up, .scroll-fade-left, .scroll-fade-right, .scroll-zoom, .scroll-rotate');
      elements.forEach(el => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowAlert(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setTimeout(() => setShowAlert(false), 5000);
  };

  const socialLinks = [
    { icon: <FaFacebook />, url: '#', color: '#3b5998' },
    { icon: <FaInstagram />, url: '#', color: '#e4405f' },
    { icon: <FaLinkedin />, url: '#', color: '#0077b5' },
    { icon: <FaWhatsapp />, url: `https://wa.me/919123860262`, color: '#25d366' }
  ];

  return (
    <section id="contact" className="section-padding" ref={sectionRef}>
      <Container>
        <Row className="justify-content-center text-center mb-5">
          <Col lg={8}>
            <h2 className="section-title scroll-fade-up title-enhance">{t.contact.title}</h2>
            <p className="section-subtitle scroll-fade-up fade-in-delay-1">{t.contact.subtitle}</p>
          </Col>
        </Row>

        <Row>
          <Col lg={8} className="mb-4">
            <div className="contact-form scroll-fade-left hover-glow animate-shimmer">
              <h4 className="mb-4">Send us a Message</h4>
              
              {showAlert && (
                <Alert variant="success" className="mb-4">
                  Thank you for your message! We will get back to you soon.
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t.contact.form.name}</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t.contact.form.email}</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter your email"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t.contact.form.phone}</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label>{t.contact.form.message}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your condition or questions"
                  />
                </Form.Group>
                
                <div className="d-flex gap-2">
                  <Button 
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = `tel:${t.hero.phone}`;
                    }}
                    size="lg" 
                    className="btn-primary-custom flex-fill hover-glow animate-bounce-in"
                  >
                    <FaPhone className="me-2" />
                    Call Now
                  </Button>
                  
                  <Button 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/bookings');
                    }}
                    size="lg" 
                    variant="success"
                    className="flex-fill hover-glow animate-bounce-in"
                  >
                    <FaCalendarAlt className="me-2" />
                    Book Now
                  </Button>
                </div>
              </Form>
            </div>
          </Col>

          <Col lg={4}>
            <div className="contact-info-section scroll-fade-right hover-lift">
              <h4 className="mb-4">{t.contact.title}</h4>
              
              <div className="contact-info-item scroll-fade-up hover-glow animate-bounce-in" style={{'--i': 0}}>
                <div className="icon float-slow animate-heartbeat">
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <h6 className="mb-1">{t.contact.info.address}</h6>
                  <p className="mb-0 scroll-fade-left">{t.contact.details.addressText}</p>
                </div>
              </div>

              <div className="contact-info-item scroll-fade-up hover-slide animate-zoom-in" style={{'--i': 1}}>
                <div className="icon animate-wobble hover-rotate">
                  <FaPhone />
                </div>
                <div>
                  <h6 className="mb-1">{t.contact.info.phone}</h6>
                  <p className="mb-0 scroll-fade-right">{t.contact.details.phoneText}</p>
                </div>
              </div>

              <div className="contact-info-item scroll-fade-up hover-bounce animate-flip-in" style={{'--i': 2}}>
                <div className="icon float-medium animate-swing">
                  <FaEnvelope />
                </div>
                <div>
                  <h6 className="mb-1">{t.contact.info.email}</h6>
                  <p className="mb-0 scroll-fade-left">{t.contact.details.emailText}</p>
                </div>
              </div>

              <div className="contact-info-item scroll-fade-up hover-tilt animate-rotate-in" style={{'--i': 3}}>
                <div className="icon animate-pulse hover-scale">
                  <FaClock />
                </div>
                <div>
                  <h6 className="mb-1">{t.contact.info.hours}</h6>
                  <p className="mb-0 scroll-fade-right">{t.contact.details.hoursText}</p>
                </div>
              </div>

              <div className="social-links stagger-animation">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link hover-bounce animate-elastic"
                    style={{ 
                      '--hover-color': social.color,
                      '--i': index 
                    }}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col>
            <div className="fade-in">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4567.818338364505!2d83.98301197597834!3d21.497584071509877!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a2117f03833766f%3A0x40b446b6f58c1aec!2sPhysio%20sphere!5e1!3m2!1sen!2sin!4v1760813726990!5m2!1sen!2sin"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '15px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Physio Sphere Location"
              ></iframe>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;